import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import { 
  ShieldCheck, 
  PlusCircle, 
  Trash2, 
  Edit3, 
  Search, 
  AlertTriangle,
  Home,
  UserCheck
} from "lucide-react";
import axios from "axios";
import { deleteApprover, getAllApprovers, setApprover } from "../../store/actions";
import { 
  Button, 
  Table, 
  Breadcrumb, 
  Modal, 
  useToast,
  Badge,
  Card
} from "../UI";
import { useTableSearch } from "../Common/useTableSearch";



const ApproverList = ({
  getAllApprovers,
  approvers,
  setApprover,
  deleteApprover
}: any) => {
  const { toast } = useToast();
  const [approverToDelete, setApproverToDelete] = useState<any>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [searchVal, setSearchVal] = useState<any>("");

  const [refreshToggle, setRefreshToggle] = useState(0);

  const fetchUsers = React.useCallback(async () => {
    const { data } = await axios.get(`api/approvers/getallapprover`);
    return { data };
  }, [refreshToggle]);

  const { filteredData, loading } = useTableSearch({
    searchVal,
    retrieve: fetchUsers,
  });

  const handleDeleteClick = (record: any) => {
    setApproverToDelete(record);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (approverToDelete) {
      try {
        await deleteApprover(approverToDelete.id || approverToDelete._id);
        setRefreshToggle(prev => prev + 1);
        toast("Approver deleted successfully", "success");
        setIsDeleteModalOpen(false);
        setApproverToDelete(null);
      } catch (error: any) {
        toast(error.message || "Failed to delete approver", "error");
      }
    }
  };

  const columns = [
    {
      title: "Approver Name",
      dataIndex: "fullName",
      key: "fullName",
      render: (text: string) => (
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-[#8dc63f]/20 flex items-center justify-center text-[#76ab32] font-bold text-xs">
            {text?.charAt(0)}
          </div>
          <span className="font-bold text-slate-700">{text}</span>
        </div>
      )
    },
    {
      title: "Approver Role",
      dataIndex: "approverRole",
      key: "approverRole",
      render: (role: string) => (
        <Badge variant={role?.includes("Manager") ? "success" : "info"}>
          {role}
        </Badge>
      )
    },
    {
      title: "Action",
      key: "operation",
      align: 'right' as const,
      render: (_: any, record: any) => (
        <div className="flex items-center justify-end gap-2">
          <Link to="/editapprover">
            <Button 
              variant="ghost" 
              size="sm" 
              className="h-10 w-10 p-0 text-amber-500 hover:text-amber-600 hover:bg-amber-50"
              onClick={() => setApprover(record)}
            >
              <Edit3 className="w-5 h-5" />
            </Button>
          </Link>
          <Button 
            variant="ghost" 
            size="sm" 
            className="h-10 w-10 p-0 text-rose-500 hover:text-rose-600 hover:bg-rose-50"
            onClick={() => handleDeleteClick(record)}
          >
            <Trash2 className="w-5 h-5" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4 animate-in fade-in duration-500">
      <div className="flex flex-col gap-2">
        <Breadcrumb 
          items={[
            { label: 'Home', to: '/dashboard' },
            { label: 'Approvers' }
          ]} 
        />
        
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 bg-[#8dc63f]/15 rounded-xl flex items-center justify-center">
              <UserCheck className="w-4 h-4 text-[#8dc63f]" />
            </div>
            <h1 className="text-xl font-bold text-slate-900 tracking-tight leading-none">Approver Management</h1>
          </div>

          <Link to="/addapprover">
            <Button className="h-8 px-3 gap-1.5 text-xs font-semibold bg-[#8dc63f] hover:bg-[#76ab32] shadow-sm">
              <PlusCircle className="w-3.5 h-3.5" />
              Add Approver
            </Button>
          </Link>
        </div>
      </div>

      <Card className="overflow-hidden border-slate-200/60 shadow-sm">
        <div className="px-3 py-2.5 border-b border-slate-100 bg-white flex items-center justify-between gap-3">
          <div className="relative group flex-1 max-w-xs">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-[#8dc63f] transition-colors" />
            <input
              type="text"
              placeholder="Search by name or role..."
              className="pl-9 pr-3 h-8 w-full bg-slate-50 border border-slate-100 rounded-lg text-xs font-normal focus:bg-white focus:border-[#8dc63f] focus:ring-2 focus:ring-[#8dc63f]/5 transition-all outline-none"
              value={searchVal}
              onChange={(e) => setSearchVal(e.target.value)}
            />
          </div>
          <div className="text-[11px] font-semibold text-slate-400 uppercase tracking-widest">
            {filteredData?.length || 0} Records
          </div>
        </div>

        <Table
          columns={columns}
          dataSource={filteredData}
          loading={loading}
          onRowClick={(record) => setApprover(record)}
        />
      </Card>

      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Confirm Deletion"
        footer={
          <div className="flex gap-3 justify-end w-full">
            <Button variant="ghost" onClick={() => setIsDeleteModalOpen(false)}>Cancel</Button>
            <Button variant="danger" onClick={confirmDelete} className="gap-2">
              <Trash2 className="w-4 h-4" />
              Delete Approver
            </Button>
          </div>
        }
      >
        <div className="flex items-center gap-4 p-1">
          <div className="w-12 h-12 rounded-full bg-rose-100 flex items-center justify-center shrink-0">
            <AlertTriangle className="w-6 h-6 text-rose-600" />
          </div>
          <div>
            <p className="text-slate-900 font-bold">Are you sure?</p>
            <p className="text-sm text-slate-500 font-medium">
              You are about to remove <span className="text-slate-900 font-bold">{approverToDelete?.fullName}</span>. This action will revoke their approval authority immediately.
            </p>
          </div>
        </div>
      </Modal>
    </div>
  );
};

const mapStateToProps = (state: any) => ({
  approvers: state.approver.approverData,
  singleApprover: state.approver.singleApprover,
});

const mapDispatchToProps = {
  getAllApprovers,
  setApprover,
  deleteApprover
};

export default connect(mapStateToProps, mapDispatchToProps)(ApproverList);




